import { useEffect, useRef, useState } from 'react';
import { X, Download, Share2, Copy } from 'lucide-react';
import { toast } from 'sonner';

/**
 * Renders a stylized "Now Playing" share card onto a canvas
 * (thumbnail + song + artist + brand + Patna quote), and offers
 * download / Web Share / copy-link actions.
 */
const ShareCard = ({ song, onClose }) => {
  const canvasRef = useRef(null);
  const [dataUrl, setDataUrl] = useState('');
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    if (!song) return;
    let cancelled = false;
    (async () => {
      try {
        const url = await renderCard(canvasRef.current, song);
        if (!cancelled) {
          setDataUrl(url);
          setBusy(false);
        }
      } catch (e) {
        console.error('Share card render failed', e);
        setBusy(false);
      }
    })();
    return () => { cancelled = true; };
  }, [song]);

  if (!song) return null;

  const download = () => {
    if (!dataUrl) return;
    const a = document.createElement('a');
    a.href = dataUrl;
    a.download = `patna-ke-presleys-${(song.title || 'song').replace(/[^a-z0-9]+/gi, '-').toLowerCase()}.png`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    toast.success('Card downloaded');
  };

  const share = async () => {
    if (!dataUrl) return;
    // Punchy Hinglish caption for WhatsApp / social share sheet
    const captions = [
      `🎶 Suniye — "${song.title}" by ${song.artist}\n\nBihar ka beat, Bollywood ka jazba 🎧\nPatna Ke Presleys pe milte hain ✨\n\n${window.location.origin}`,
      `Abhi bajaya "${song.title}" 🔥\nPure Patna vibes only 🪘\n\nSuniye aur jhoomiye — ${window.location.origin}`,
      `"${song.title}" — ${song.artist} 🎙️\nPatna Ke Presleys · गंगा किनारे बसा है, गीतों का घराना\n\n${window.location.origin}`,
    ];
    const text = captions[Math.floor(Math.random() * captions.length)];
    try {
      const blob = await (await fetch(dataUrl)).blob();
      const file = new File([blob], 'patna-ke-presleys.png', { type: 'image/png' });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          files: [file],
          title: `${song.title} — Patna Ke Presleys`,
          text,
        });
      } else if (navigator.share) {
        await navigator.share({
          title: `${song.title} — Patna Ke Presleys`,
          text,
          url: window.location.origin,
        });
      } else {
        await navigator.clipboard.writeText(text);
        toast.info('Share sheet unavailable — Hinglish caption copied to clipboard');
      }
    } catch (e) {
      if (e && e.name !== 'AbortError') toast.error('Could not share');
    }
  };

  const copyLink = async () => {
    try {
      const caption = `🎶 "${song.title}" — ${song.artist}\nPatna Ke Presleys · ${window.location.origin}`;
      await navigator.clipboard.writeText(caption);
      toast.success('Caption + link copied');
    } catch { toast.error('Copy failed'); }
  };

  return (
    <div
      data-testid="share-card-modal"
      className="fixed inset-0 z-[60] bg-[#120505]/95 backdrop-blur-md slide-up flex flex-col"
    >
      <div className="flex items-center justify-between p-4 border-b border-[#3D1A1A]">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">Share Card</div>
          <div className="font-display text-lg text-[#FFF3E0]">Send the vibes</div>
        </div>
        <button
          data-testid="share-close-btn"
          onClick={onClose}
          className="w-11 h-11 rounded-full flex items-center justify-center hover:bg-[#220A0A]"
          aria-label="Close"
        >
          <X className="w-5 h-5 text-[#FFF3E0]" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 flex flex-col items-center gap-4">
        <div className="w-full max-w-sm">
          <canvas
            ref={canvasRef}
            width={1080}
            height={1350}
            className="w-full h-auto rounded-2xl shadow-[0_20px_80px_rgba(255,0,127,0.25)] border border-[#3D1A1A]"
            data-testid="share-card-canvas"
          />
          {busy && <div className="mt-3 text-center text-xs text-[#BFA5A5]">Preparing your card...</div>}
        </div>

        <div className="w-full max-w-sm grid grid-cols-3 gap-2 pb-2">
          <button
            data-testid="share-download-btn"
            onClick={download}
            disabled={busy}
            className="h-12 rounded-full bg-[#FFB800] text-[#120505] font-bold flex items-center justify-center gap-1 disabled:opacity-50 hover:scale-[1.02] active:scale-[0.98] transition-transform"
          >
            <Download className="w-4 h-4" /> <span className="text-xs">Save</span>
          </button>
          <button
            data-testid="share-share-btn"
            onClick={share}
            disabled={busy}
            className="h-12 rounded-full bg-[#FF007F] text-[#FFF3E0] font-bold flex items-center justify-center gap-1 disabled:opacity-50 hover:scale-[1.02] active:scale-[0.98] transition-transform"
          >
            <Share2 className="w-4 h-4" /> <span className="text-xs">Share</span>
          </button>
          <button
            data-testid="share-copy-btn"
            onClick={copyLink}
            className="h-12 rounded-full border border-[#3D1A1A] text-[#FFF3E0] font-bold flex items-center justify-center gap-1 hover:border-[#FFB800] transition-colors"
          >
            <Copy className="w-4 h-4" /> <span className="text-xs">Link</span>
          </button>
        </div>
      </div>
    </div>
  );
};

async function loadImage(url) {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
}

async function renderCard(canvas, song) {
  const ctx = canvas.getContext('2d');
  const W = canvas.width, H = canvas.height;

  // Background: deep maroon with soft gold radial + noise-like specks
  ctx.fillStyle = '#120505';
  ctx.fillRect(0, 0, W, H);

  // Gold glow top-right
  const g1 = ctx.createRadialGradient(W * 0.85, H * 0.15, 30, W * 0.85, H * 0.15, W * 0.9);
  g1.addColorStop(0, 'rgba(255,184,0,0.35)');
  g1.addColorStop(1, 'rgba(255,184,0,0)');
  ctx.fillStyle = g1;
  ctx.fillRect(0, 0, W, H);

  // Pink glow bottom-left
  const g2 = ctx.createRadialGradient(W * 0.15, H * 0.9, 20, W * 0.15, H * 0.9, W * 0.9);
  g2.addColorStop(0, 'rgba(255,0,127,0.35)');
  g2.addColorStop(1, 'rgba(255,0,127,0)');
  ctx.fillStyle = g2;
  ctx.fillRect(0, 0, W, H);

  // Border
  ctx.strokeStyle = 'rgba(255,184,0,0.35)';
  ctx.lineWidth = 3;
  ctx.strokeRect(30, 30, W - 60, H - 60);

  // Brand top-left
  ctx.fillStyle = '#FFB800';
  ctx.font = '600 34px "DM Sans", sans-serif';
  ctx.textBaseline = 'top';
  ctx.fillText('◐ PATNA KE PRESLEYS', 80, 80);
  ctx.fillStyle = '#BFA5A5';
  ctx.font = '400 26px "DM Sans", sans-serif';
  ctx.fillText('Bihar ka beat, Bollywood ka jazba', 80, 128);

  // Thumbnail
  const thumb = await loadImage(song.thumbnail_url || `https://i.ytimg.com/vi/${song.youtube_video_id}/hqdefault.jpg`);
  const thumbSize = 720;
  const tx = (W - thumbSize) / 2;
  const ty = 220;
  // Rounded rectangle clip
  const r = 32;
  ctx.save();
  ctx.beginPath();
  ctx.moveTo(tx + r, ty);
  ctx.arcTo(tx + thumbSize, ty, tx + thumbSize, ty + thumbSize, r);
  ctx.arcTo(tx + thumbSize, ty + thumbSize, tx, ty + thumbSize, r);
  ctx.arcTo(tx, ty + thumbSize, tx, ty, r);
  ctx.arcTo(tx, ty, tx + thumbSize, ty, r);
  ctx.closePath();
  ctx.clip();
  if (thumb) {
    // Cover-fit
    const iw = thumb.naturalWidth || thumb.width, ih = thumb.naturalHeight || thumb.height;
    const scale = Math.max(thumbSize / iw, thumbSize / ih);
    const dw = iw * scale, dh = ih * scale;
    ctx.drawImage(thumb, tx + (thumbSize - dw) / 2, ty + (thumbSize - dh) / 2, dw, dh);
  } else {
    ctx.fillStyle = '#220A0A';
    ctx.fillRect(tx, ty, thumbSize, thumbSize);
  }
  ctx.restore();
  ctx.strokeStyle = 'rgba(255,255,255,0.06)';
  ctx.lineWidth = 2;
  ctx.strokeRect(tx, ty, thumbSize, thumbSize);

  // Now Playing label
  ctx.fillStyle = '#FF007F';
  ctx.font = '600 22px "DM Sans", sans-serif';
  ctx.fillText('ABHI BAJ RAHA HAI', 80, ty + thumbSize + 40);

  // Title (wrap up to 2 lines)
  ctx.fillStyle = '#FFF3E0';
  ctx.font = '900 56px "Playfair Display", serif';
  wrapText(ctx, song.title || 'Untitled', 80, ty + thumbSize + 90, W - 160, 68, 2);

  // Artist
  ctx.fillStyle = '#FFB800';
  ctx.font = '500 32px "DM Sans", sans-serif';
  ctx.fillText(song.artist || 'Unknown', 80, ty + thumbSize + 90 + 68 * 2 + 16);

  // Footer quote strip
  ctx.fillStyle = '#3D1A1A';
  ctx.fillRect(80, H - 170, W - 160, 2);
  ctx.fillStyle = '#FFF3E0';
  ctx.font = 'italic 28px "Playfair Display", serif';
  ctx.fillText('"Ganga kinare basa hai, geeton ka gharana"', 80, H - 135);
  ctx.fillStyle = '#BFA5A5';
  ctx.font = '400 22px "DM Sans", sans-serif';
  ctx.fillText('patnakepresleys.app', 80, H - 92);

  return canvas.toDataURL('image/png');
}

function wrapText(ctx, text, x, y, maxWidth, lineHeight, maxLines) {
  const words = String(text).split(' ');
  let line = '';
  let lines = 0;
  for (let i = 0; i < words.length; i++) {
    const test = line ? line + ' ' + words[i] : words[i];
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, y + lines * lineHeight);
      line = words[i];
      lines++;
      if (lines >= maxLines - 1) break;
    } else {
      line = test;
    }
  }
  if (lines < maxLines) {
    // Truncate remaining if any words left
    if (line) {
      // If we truncated the loop, ensure ellipsis
      let final = line;
      // Add trailing words as ellipsis if needed
      const rest = words.slice(words.indexOf(line.split(' ').pop()) + 1);
      if (lines === maxLines - 1 && rest.length && ctx.measureText(final + '…').width < maxWidth) {
        final = final + '…';
      }
      ctx.fillText(final, x, y + lines * lineHeight);
    }
  }
}

export default ShareCard;
